import React from "react";
//#region types
import { FormFieldContainerMemoizedProps } from "models/propTypes/genericFormProps";
import { FormHelper } from "utils/helpers";
//#endregion
//#region components
import useFormFieldMessage from "./hooks/useFormFieldMessage";
import useCheckRequired from "./hooks/useCheckRequired";
import FormFieldContainer from "./formFieldContainer";
import MessageBubble from "../messages/messageBubble";
import { getIn, useFormikContext } from "formik";
import { Grid } from "@mui/material";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

function FormFieldContainerNonMemoized<T extends Record<string, any>>(props: FormFieldContainerMemoizedProps<T>) {
  const {
    value,
    field,
    name: fieldName,
    nameForOptionalCheck,
    errors,
    touched,
    hideOptionalIndicator,
    overrideOptional,
    validationSchema,
    refValues,
  } = props;

  const [disabled, setDisabled] = React.useState(false);
  const [readOnly, setReadOnly] = React.useState(false);
  const [isUserInput, setIsUserInput] = React.useState(false);
  const [loading, setIsLoading] = React.useState(false);
  const formikContext = useFormikContext<T>();
  const { setFieldTouched, setFieldValue, values, dirty, touched: formTouched } = formikContext;

  React.useEffect(() => {
    if (
      field.enableOnChangeAlways ||
      dirty ||
      (typeof formTouched === "object" && Object.keys(formTouched).length > 0)
    ) {
      field.onChangeHandler && field.onChangeHandler(fieldName, value, formikContext, isUserInput);
    }
    setIsUserInput(false);
  }, [value]);

  const getHideErrorBubble = (hideErrorBubbleProp?: boolean) =>
    (hideErrorBubbleProp && !field.overrideHideErrorBubble) || field.hideErrorBubble;

  const formFieldMessage = useFormFieldMessage(
    {
      value,
      values,
      errors,
      touched,
      customMessageHandler: field.customMessageHandler,
      helperText: field.helperText,
    },
    getHideErrorBubble(props.hideErrorBubble)
  );

  const messagePlacement = React.useMemo(
    () =>
      field.widthColumnCount && !props.inline
        ? "right"
        : props.formGridLayout && !props.formGridLayout.message
          ? "top"
          : props.messagePlacement,
    []
  );

  React.useEffect(() => {
    const disabledNow = getFieldDisabled();
    if (disabledNow !== disabled) setDisabled(disabledNow);
    const readOnlyNow = getFieldReadOnly();
    if (readOnlyNow !== readOnly) setReadOnly(readOnlyNow);
    const loadingNow = getFieldLoading();
    if (loadingNow !== loading) setIsLoading(loadingNow);
  }, [values, refValues]);

  const getFieldReadOnly = () => {
    const fieldReadOnly = field.readonly;

    let isReadOnly = undefined;
    if (field.readonly !== undefined) {
      if (typeof fieldReadOnly === "boolean") {
        isReadOnly = fieldReadOnly as boolean;
      } else if (typeof fieldReadOnly === "function") {
        isReadOnly = fieldReadOnly(refValues ? refValues : values);
      } else {
      }
    }
    return !!isReadOnly;
  };

  const getFieldDisabled = () => {
    const fieldDisableProps = field.disabled ?? props.disabled;
    let isDisabled = undefined;
    if (fieldDisableProps !== undefined) {
      if (typeof fieldDisableProps === "boolean") {
        isDisabled = fieldDisableProps as boolean;
      } else if (typeof fieldDisableProps === "function") {
        isDisabled = fieldDisableProps(refValues ? refValues : values);
      } else {
        isDisabled =
          fieldDisableProps &&
          getIn(
            values,
            FormHelper.formatReferenceFieldName(fieldName, fieldDisableProps.referenceFieldName as string)
            // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
          ) == fieldDisableProps.disableWhenEqualTo;
      }
    }
    return !!isDisabled;
  };

  const getFieldLoading = () => {
    return field.loading ? field.loading(values) : false;
  };

  const [required, setRequired] = React.useState<boolean>();
  const checkRequired = useCheckRequired(validationSchema);
  const getRequired = () => {
    if (!hideOptionalIndicator) {
      const fieldInfo = FormHelper.getTableRowFieldInfo(nameForOptionalCheck ?? fieldName);
      return overrideOptional === undefined
        ? checkRequired.getFieldRequired(
            String(nameForOptionalCheck ?? fieldName).replaceAll(/\[[0-9]+\]/g, ""),
            fieldInfo ? `${fieldInfo.fieldName}[${fieldInfo.rowIndex}]` : undefined
          )
        : !overrideOptional;
    }
    return true;
  };

  React.useEffect(() => {
    const currentRequired = getRequired();
    if (required !== currentRequired) setRequired(currentRequired);
  }, [values, fieldName]);

  const onChangeFromUIHandler = (updatedValue: any) => {
    setIsUserInput(true);
  };

  const component = (
    <FormFieldContainer
      fieldRef={props.fieldRef}
      name={props.name}
      field={props.field}
      hideErrorBubble={getHideErrorBubble(props.hideErrorBubble)}
      hideSuccess={props.hideSuccess}
      messagePlacement={props.messagePlacement}
      invertedDisplayType={props.invertedDisplayType}
      inline={props.inline}
      enableControlLables={props.enableControlLables}
      contentWidth={props.contentWidth}
      formGridLayout={props.formGridLayout}
      layout={props.layout}
      validationSchema={props.validationSchema}
      overrideOptional={props.overrideOptional}
      hideOptionalIndicator={props.hideOptionalIndicator}
      focusField={props.focusField}
      value={props.value}
      errors={props.errors}
      touched={props.touched}
      setFieldTouched={setFieldTouched}
      setFieldValue={setFieldValue}
      disabled={disabled}
      optional={!required}
      readonly={readOnly}
      onChangeFromUIHandler={onChangeFromUIHandler}
      loading={loading}
    />
  );

  return required !== undefined ? (
    <Grid
      container
      item
      sm={field.widthColumnCount}
      className={clsx(field.rightAlign ? cssExports.formFieldRightAlign : "", field.classes?.formField)}
      data-testid={`${props.name}FormField`}
    >
      {field.customContentBefore ? field.customContentBefore(values) : <></>}
      {getHideErrorBubble(props.hideErrorBubble) ? (
        component
      ) : (
        <Grid
          container
          direction={messagePlacement === "right" ? "row" : "column-reverse"}
          className={
            messagePlacement === "right"
              ? cssExports.formFieldMessageAttachElemRight
              : cssExports.formFieldMessageAttachElemTop
          }
        >
          <Grid container item xs>
            {component}
          </Grid>
          <MessageBubble
            message={formFieldMessage?.message}
            type={formFieldMessage?.type}
            placement={messagePlacement}
          ></MessageBubble>
        </Grid>
      )}
      {field.customContentAfter ? field.customContentAfter(values) : <></>}
    </Grid>
  ) : (
    <></>
  );
}

export default FormFieldContainerNonMemoized;
